// ==================== WEBSOCKET SERVICE ====================
// Real-time WebSocket connection for live messaging updates

import { io, Socket } from 'socket.io-client';
import { APP_CONSTANTS, getApiUrl } from '../constants';

// ==================== EVENT NAMES ====================

/** Dispatched on `window` by the API client after it stores a refreshed access token. */
export const TOKEN_REFRESHED_EVENT = 'amplify:token-refreshed';

export const WS_EVENTS = {
  // Connection events
  CONNECTION: 'connection',
  DISCONNECT: 'disconnect',
  
  // Authentication events
  AUTHENTICATE: 'authenticate',
  AUTHENTICATED: 'authenticated',
  AUTH_ERROR: 'auth_error',
  
  // Room events
  JOIN_CONVERSATION: 'join_conversation',
  LEAVE_CONVERSATION: 'leave_conversation',
  
  // WhatsApp Message events
  WHATSAPP_MESSAGE_RECEIVED: 'whatsapp:message:received',
  WHATSAPP_MESSAGE_SENT: 'whatsapp:message:sent',
  WHATSAPP_MESSAGE_DELIVERED: 'whatsapp:message:delivered',
  WHATSAPP_MESSAGE_READ: 'whatsapp:message:read',
  WHATSAPP_MESSAGE_FAILED: 'whatsapp:message:failed',

  // The organization's plan/add-ons changed (e.g. by an admin) — refetch the user.
  ACCOUNT_UPDATED: 'account:updated',
} as const;

// ==================== PAYLOAD INTERFACES ====================

export interface WhatsAppMessagePayload {
  id: string;
  contactId: string;
  organizationId: string;
  direction: 'INBOUND' | 'OUTBOUND';
  type: 'text' | 'image' | 'video' | 'audio' | 'document' | 'sticker' | 'location' | 'template';
  content: string | null;
  mediaUrl: string | null;
  mediaType: string | null;
  status: 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  metadata: Record<string, unknown> | null;
  createdAt: string;
  contact: {
    id: string;
    phoneNumber: string;
    name: string | null;
  };
}

export interface MessageStatusUpdatePayload {
  messageId: string;
  contactId: string;
  organizationId: string;
  status: 'SENT' | 'DELIVERED' | 'READ' | 'FAILED';
  timestamp: string;
  errorMessage?: string;
}

/** Emitted by the backend as `leadgen:<event>` from the Lead Generation agent webhooks. */
export interface LeadGenEventPayload {
  event: string; // e.g. "lead.discovered", "run.completed", "outreach.replied"
  eventId?: string;
  timestamp?: string;
  data?: Record<string, unknown>;
}

// ==================== WEBSOCKET SERVICE ====================

class WebSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private isAuthenticated = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private authRetries = 0;
  private authRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenListenerBound = false;
  
  // Event listeners
  private messageListeners: ((message: WhatsAppMessagePayload) => void)[] = [];
  private statusListeners: ((update: MessageStatusUpdatePayload) => void)[] = [];
  private connectionListeners: ((connected: boolean) => void)[] = [];
  private leadGenListeners: ((event: LeadGenEventPayload) => void)[] = [];
  private accountListeners: (() => void)[] = [];

  /**
   * Initialize and connect to WebSocket server
   */
  connect(): void {
    if (this.socket?.connected) {
      // Connected but the auth handshake failed earlier (e.g. an expired token at
      // page load) — try again now that a fresh token may be in storage.
      if (!this.isAuthenticated) this.authenticate();
      return;
    }
    if (this.socket) {
      this.socket.connect();
      return;
    }

    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return;
    }

    // The API client refreshes the access token in the background; the socket
    // must re-authenticate with it or the tab silently stops receiving events.
    if (!this.tokenListenerBound && typeof window !== 'undefined') {
      window.addEventListener(TOKEN_REFRESHED_EVENT, () => this.reauthenticate());
      this.tokenListenerBound = true;
    }

    // Get WebSocket URL (same as API URL but without /api/v1)
    const apiUrl = getApiUrl();
    const wsUrl = apiUrl.replace('/api/v1', '');

    this.socket = io(wsUrl, {
      // Start with polling, then upgrade to websocket (more reliable through load balancers)
      transports: ['polling', 'websocket'],
      upgrade: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      // Needed for cross-origin requests with credentials
      withCredentials: true,
      // Increase timeout for production environments
      timeout: 20000,
    });

    this.setupEventListeners();
  }

  /**
   * Setup socket event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.notifyConnectionListeners(true);
      
      // Authenticate after connection
      this.authenticate();
    });

    this.socket.on('disconnect', () => {
      this.isConnected = false;
      this.isAuthenticated = false;
      this.notifyConnectionListeners(false);
    });

    this.socket.on('connect_error', () => {
      this.reconnectAttempts++;
    });

    // Authentication events
    this.socket.on(WS_EVENTS.AUTHENTICATED, () => {
      this.isAuthenticated = true;
      this.authRetries = 0;
      if (this.authRetryTimer) { clearTimeout(this.authRetryTimer); this.authRetryTimer = null; }
      // Notify connection listeners that we're fully connected and authenticated
      this.notifyConnectionListeners(true);
    });

    this.socket.on(WS_EVENTS.AUTH_ERROR, () => {
      this.isAuthenticated = false;
      this.notifyConnectionListeners(false);
      // Usually a stale token at page load; the API client refreshes it within
      // moments, so retry with backoff instead of staying deaf for the session.
      if (this.authRetries < 6 && !this.authRetryTimer) {
        const delay = Math.min(30_000, 2_000 * 2 ** this.authRetries);
        this.authRetries += 1;
        this.authRetryTimer = setTimeout(() => {
          this.authRetryTimer = null;
          this.authenticate();
        }, delay);
      }
    });

    // Message events
    this.socket.on(WS_EVENTS.WHATSAPP_MESSAGE_RECEIVED, (message: WhatsAppMessagePayload) => {
      this.notifyMessageListeners(message);
    });

    // Status update events
    this.socket.on(WS_EVENTS.WHATSAPP_MESSAGE_SENT, (update: MessageStatusUpdatePayload) => {
      this.notifyStatusListeners(update);
    });

    this.socket.on(WS_EVENTS.WHATSAPP_MESSAGE_DELIVERED, (update: MessageStatusUpdatePayload) => {
      this.notifyStatusListeners(update);
    });

    this.socket.on(WS_EVENTS.WHATSAPP_MESSAGE_READ, (update: MessageStatusUpdatePayload) => {
      this.notifyStatusListeners(update);
    });

    this.socket.on(WS_EVENTS.WHATSAPP_MESSAGE_FAILED, (update: MessageStatusUpdatePayload) => {
      this.notifyStatusListeners(update);
    });

    this.socket.on(WS_EVENTS.ACCOUNT_UPDATED, () => {
      this.accountListeners.forEach(cb => {
        try {
          cb();
        } catch {
          // Silently handle listener errors
        }
      });
    });

    // Lead Generation agent events — backend emits them as `leadgen:<event>`.
    this.socket.onAny((eventName: string, payload: LeadGenEventPayload) => {
      if (typeof eventName === 'string' && eventName.startsWith('leadgen:')) {
        this.notifyLeadGenListeners({
          event: payload?.event || eventName.replace('leadgen:', ''),
          eventId: payload?.eventId,
          timestamp: payload?.timestamp,
          data: payload?.data,
        });
      }
    });
  }

  /** Re-run the auth handshake with the token currently in storage. */
  reauthenticate(): void {
    if (this.authRetryTimer) { clearTimeout(this.authRetryTimer); this.authRetryTimer = null; }
    this.authRetries = 0;
    if (this.socket?.connected) this.authenticate();
    else this.connect();
  }

  /**
   * Authenticate with the WebSocket server
   */
  private authenticate(): void {
    const token = localStorage.getItem(APP_CONSTANTS.STORAGE_KEYS.ACCESS_TOKEN);
    if (!token || !this.socket) {
      return;
    }

    this.socket.emit(WS_EVENTS.AUTHENTICATE, { token });
  }

  /**
   * Join a conversation room for real-time updates
   */
  joinConversation(contactId: string): void {
    if (!this.socket || !this.isAuthenticated) {
      return;
    }

    this.socket.emit(WS_EVENTS.JOIN_CONVERSATION, { contactId });
  }

  /**
   * Leave a conversation room
   */
  leaveConversation(contactId: string): void {
    if (!this.socket) return;

    this.socket.emit(WS_EVENTS.LEAVE_CONVERSATION, { contactId });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.isAuthenticated = false;
    }
  }

  /**
   * Check if connected and authenticated
   */
  isReady(): boolean {
    return this.isConnected && this.isAuthenticated;
  }

  /**
   * Get current connection state
   */
  getConnectionState(): boolean {
    return this.isConnected && this.isAuthenticated;
  }

  // ==================== LISTENER MANAGEMENT ====================

  /**
   * Subscribe to new messages
   */
  onMessage(callback: (message: WhatsAppMessagePayload) => void): () => void {
    this.messageListeners.push(callback);
    return () => {
      this.messageListeners = this.messageListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to message status updates
   */
  onStatusUpdate(callback: (update: MessageStatusUpdatePayload) => void): () => void {
    this.statusListeners.push(callback);
    return () => {
      this.statusListeners = this.statusListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to connection status changes
   */
  onConnectionChange(callback: (connected: boolean) => void): () => void {
    this.connectionListeners.push(callback);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to Lead Generation agent events (leadgen:*).
   */
  onLeadGenEvent(callback: (event: LeadGenEventPayload) => void): () => void {
    this.leadGenListeners.push(callback);
    return () => {
      this.leadGenListeners = this.leadGenListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Subscribe to account changes (plan / add-ons updated elsewhere, e.g. by an admin).
   */
  onAccountUpdated(callback: () => void): () => void {
    this.accountListeners.push(callback);
    return () => {
      this.accountListeners = this.accountListeners.filter(cb => cb !== callback);
    };
  }

  // ==================== PRIVATE NOTIFY METHODS ====================

  private notifyMessageListeners(message: WhatsAppMessagePayload): void {
    this.messageListeners.forEach(cb => {
      try {
        cb(message);
      } catch {
        // Silently handle listener errors
      }
    });
  }

  private notifyStatusListeners(update: MessageStatusUpdatePayload): void {
    this.statusListeners.forEach(cb => {
      try {
        cb(update);
      } catch {
        // Silently handle listener errors
      }
    });
  }

  private notifyConnectionListeners(connected: boolean): void {
    this.connectionListeners.forEach(cb => {
      try {
        cb(connected);
      } catch {
        // Silently handle listener errors
      }
    });
  }

  private notifyLeadGenListeners(event: LeadGenEventPayload): void {
    this.leadGenListeners.forEach(cb => {
      try {
        cb(event);
      } catch {
        // Silently handle listener errors
      }
    });
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
export default websocketService;
